import React, { useState, useRef } from 'react';
import { Text, View, StyleSheet, ActivityIndicator, TouchableOpacity, Platform } from 'react-native';
import { BarCodeScanner } from 'expo-barcode-scanner';
import { Camera } from 'expo-camera';
import jsQR from 'jsqr';

import axios from 'axios';

import Color from '../values/Color';
import Constant from '../values/Constant';
import helpers from "../utils/Helper";

import i18n from 'i18n-js';

const getDeviceType = () => {
    const ua = navigator.userAgent || '';
    if (/HarmonyOS/i.test(ua) || /HUAWEI/i.test(ua) || /HONOR/i.test(ua)) return 'harmonyos';
    if (/iPhone|iPad|iPod/i.test(ua)) return 'ios';
    if (/Android/i.test(ua)) return 'android';
    return 'generic';
};

const getLocationHint = (langObj) => {
    const device = getDeviceType();
    switch (device) {
        case 'ios': return langObj?.t('enable_location_hint') || '';
        case 'android': return langObj?.t('enable_location_hint_android') || '';
        case 'harmonyos': return langObj?.t('enable_location_hint_harmonyos') || '';
        default: return langObj?.t('enable_location_hint_generic') || '';
    }
};

const styles = StyleSheet.create({
    container: {
        flex: 1,
        flexDirection: 'column',
        justifyContent: 'center',
    },
    centerOnScreen: {
        margin: 'auto'
    },
    reminderContainer: {
        flex: 1,
        backgroundColor: Color.TRANSPARENT,
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
    },
    reminderRect: {
        width: '80%',
        height: '45%',
        border: Color.WHITE,
        borderWidth: 5,
        borderStyle: 'dashed',
        backgroundColor: Color.BLACK_TRANSPARENT,
        marginBottom: 20,
        flexDirection: 'column',
        justifyContent: 'center'
    },
    redLine: {
        backgroundColor: Color.RED,
        height: 1,
        width: '100%'
    },
    reminderText: {
        fontSize: 20,
        color: Color.WHITE
    },
    headerImportBtn: {
        paddingRight: 12,
    },
    headerImportText: {
        color: Color.ORANGE,
        fontSize: 16,
        fontWeight: 'bold',
    },
    permissionErrorContainer: {
        flex: 1,
        backgroundColor: Color.BLACK,
        justifyContent: 'center',
        alignItems: 'center',
        paddingHorizontal: 30,
    },
    permissionErrorText: {
        color: Color.WHITE,
        fontSize: 18,
        textAlign: 'center',
        marginBottom: 16,
    },
    permissionHintText: {
        color: Color.LIGHT_GRAY,
        fontSize: 16,
        textAlign: 'center',
    },
    locationBanner: {
        position: 'absolute',
        bottom: 0,
        left: 0,
        right: 0,
        backgroundColor: Color.ORANGE,
        paddingVertical: 14,
        paddingHorizontal: 20,
        alignItems: 'center',
        zIndex: 10,
    },
    locationBannerText: {
        color: Color.WHITE,
        fontSize: 16,
        fontWeight: 'bold',
        textAlign: 'center',
    },
});

// Module-level cache so camera permission survives component remounts
let cachedCameraPermission = null;

export default function QRScreen({ navigation, route }) {
    const [hasCameraPermission, setHasCameraPermission] = useState(cachedCameraPermission);
    const [scanned, setScanned] = useState(false);
    const [location, setLocation] = useState(null);
    const [type, setType] = useState(Camera.Constants.Type.back);
    const [lang, setLang] = useState(null);
    const [processingImage, setProcessingImage] = useState(false);
    const [locationLoading, setLocationLoading] = useState(false);
    const fileInputRef = useRef(null);

    // Set headerRight import button
    React.useEffect(() => {
        navigation.setOptions({
            headerRight: () => (
                <TouchableOpacity
                    style={styles.headerImportBtn}
                    onPress={() => {
                        if (fileInputRef.current) {
                            fileInputRef.current.click();
                        }
                    }}
                >
                    <Text style={styles.headerImportText}>
                        {lang?.t('import_qr_photo') || 'Import'}
                    </Text>
                </TouchableOpacity>
            ),
        });
    }, [navigation, lang]);

    React.useEffect(() => {
        (async () => {
            await helpers.getI18nConfigs(i18n);
            setLang(i18n);
        })();

        // Component mounts fresh each time (unmountOnBlur: true),
        // so no need for a focus listener — just init directly.
        setScanned(false);

        if (cachedCameraPermission === null) {
            Camera.requestCameraPermissionsAsync().then(({ status }) => {
                const granted = status === "granted";
                cachedCameraPermission = granted;
                setHasCameraPermission(granted);
            });
        }

        // Try to get location silently on mount.
        // Works on Android; iOS 26 / HarmonyOS may fail silently — banner will show.
        if (navigator.geolocation) {
            navigator.geolocation.getCurrentPosition(
                (position) => {
                    setLocation({
                        coords: {
                            latitude: position.coords.latitude,
                            longitude: position.coords.longitude,
                        }
                    });
                },
                () => { /* fail silently — banner will show */ },
                { enableHighAccuracy: false, timeout: 5000, maximumAge: 60000 }
            );
        }
    }, []);

    // Request location — MUST be called synchronously from a direct user tap handler.
    // No async/await before the geolocation call — the browser needs to see it
    // in the same call stack as the tap event.
    const handleEnableLocation = () => {
        if (locationLoading || !navigator.geolocation) {
            if (!navigator.geolocation) {
                alert(lang?.t('no_gps_access'));
            }
            return;
        }
        setLocationLoading(true);

        navigator.geolocation.getCurrentPosition(
            (position) => {
                setLocation({
                    coords: {
                        latitude: position.coords.latitude,
                        longitude: position.coords.longitude,
                    }
                });
                setLocationLoading(false);
                setScanned(false); // Resume scanning now that we have location
            },
            (error) => {
                console.log('Geolocation error:', error.code, error.message);
                alert(lang?.t('no_gps_access') + '\n\n' + getLocationHint(lang));
                setLocationLoading(false);
            },
            { enableHighAccuracy: false, timeout: 15000, maximumAge: 60000 }
        );
    };

    const handleBarCodeScanned = ({ type, data }) => {
        helpers.checkNetworkBeforeAction(() => {
            setScanned(true);
            validateQRCode(data);
        });
    };

    const decodeQRFromCanvas = (canvas) => {
        const ctx = canvas.getContext('2d');
        const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
        return jsQR(imageData.data, imageData.width, imageData.height);
    };

    const tryDecodeQR = (img) => {
        const MAX_SIZE = 1000;
        let width = img.width;
        let height = img.height;

        if (width > MAX_SIZE || height > MAX_SIZE) {
            const scale = MAX_SIZE / Math.max(width, height);
            width = Math.round(width * scale);
            height = Math.round(height * scale);
        }

        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');

        ctx.drawImage(img, 0, 0, width, height);
        let code = decodeQRFromCanvas(canvas);
        if (code?.data) return code;

        const imageData = ctx.getImageData(0, 0, width, height);
        const data = imageData.data;
        for (let i = 0; i < data.length; i += 4) {
            let gray = 0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2];
            gray = gray > 128 ? 255 : 0;
            data[i] = gray;
            data[i + 1] = gray;
            data[i + 2] = gray;
        }
        ctx.putImageData(imageData, 0, 0);
        code = decodeQRFromCanvas(canvas);
        if (code?.data) return code;

        if (img.width > MAX_SIZE || img.height > MAX_SIZE) {
            canvas.width = img.width;
            canvas.height = img.height;
            ctx.drawImage(img, 0, 0);
            code = decodeQRFromCanvas(canvas);
            if (code?.data) return code;
        }

        return null;
    };

    const handleFileSelected = (event) => {
        const file = event.target.files?.[0];
        if (!file) return;
        event.target.value = '';
        setProcessingImage(true);

        const reader = new FileReader();
        reader.onload = (e) => {
            const img = new Image();
            img.onload = () => {
                try {
                    const code = tryDecodeQR(img);
                    setProcessingImage(false);
                    if (code && code.data) {
                        helpers.checkNetworkBeforeAction(() => {
                            setScanned(true);
                            validateQRCode(code.data);
                        });
                    } else {
                        alert(lang?.t('no_qr_code_found'));
                    }
                } catch (err) {
                    console.log('QR decode error:', err);
                    setProcessingImage(false);
                    alert(lang?.t('no_qr_code_found'));
                }
            };
            img.onerror = () => {
                setProcessingImage(false);
                alert(lang?.t('no_qr_code_found'));
            };
            img.src = e.target.result;
        };
        reader.onerror = () => {
            setProcessingImage(false);
            alert(lang?.t('no_qr_code_found'));
        };
        reader.readAsDataURL(file);
    };

    const validateQRCode = async (qrcode) => {
        let loc = location;

        // If location not available, try to get it now
        if (loc == null && navigator.geolocation) {
            try {
                loc = await new Promise((resolve, reject) => {
                    navigator.geolocation.getCurrentPosition(
                        (pos) => resolve({
                            coords: {
                                latitude: pos.coords.latitude,
                                longitude: pos.coords.longitude,
                            }
                        }),
                        reject,
                        { enableHighAccuracy: false, timeout: 15000, maximumAge: 60000 }
                    );
                });
                setLocation(loc);
            } catch (err) {
                // Location failed — tell user to tap the banner
                // Keep scanned=true so camera doesn't re-scan and loop
                alert(lang?.t('tap_to_enable_location'));
                return;
            }
        }

        if (loc == null) {
            alert(lang?.t('tap_to_enable_location'));
            return;
        }

        const config = await helpers.getRequestHeader();

        let req = new Object();
        req.altitude = 0.0;
        req.latitude = loc.coords.latitude;
        req.longitude = loc.coords.longitude;
        req.qrcode = qrcode.replace(/\s/g, "+");

        axios.post(Constant.API_URL + Constant.API_PATH + "/checkin/qrcode/validate", req, config)
            .then(response => {
                if (!response.data.data) {
                    alert(response.data.message);
                    setScanned(false);
                } else {
                    let data = response.data.data;
                    let chkInData = new Object();
                    chkInData.checkin_token = data.checkin_token;
                    chkInData.altitude = 0.0;
                    chkInData.latitude = req.latitude;
                    chkInData.longitude = req.longitude;

                    navigation.navigate({
                        name: Constant.SCREEN_STEP_1,
                        params: { chkInData: chkInData },
                    });
                }
            })
            .catch(error => {
                helpers.showAPIError(error);
                setScanned(false);
            });
    }

    const renderFileInput = () => (
        Platform.OS === 'web' ? (
            <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                style={{ display: 'none' }}
                onChange={handleFileSelected}
            />
        ) : null
    );

    if (hasCameraPermission === null) {
        return (
            <View style={styles.container}>
                {renderFileInput()}
                <ActivityIndicator style={styles.centerOnScreen} size="large" color={Color.PEACH} />
            </View>
        );
    }

    if (hasCameraPermission === false) {
        return (
            <View style={styles.container}>
                {renderFileInput()}
                <View style={styles.permissionErrorContainer}>
                    <Text style={styles.permissionErrorText}>
                        {lang?.t('no_camera_access')}{'\n'}{lang?.t('enable_camera_hint')}
                    </Text>
                    <Text style={styles.permissionHintText}>{lang?.t('import_qr_hint')}</Text>
                </View>
            </View>
        );
    }

    return (
        <View style={styles.container}>
            {renderFileInput()}

            <Camera
                type={type}
                onBarCodeScanned={scanned ? undefined : handleBarCodeScanned}
                barCodeScannerEnabled={true}
                barCodeScannerSettings={{
                    barCodeTypes: [BarCodeScanner.Constants.BarCodeType.qr],
                }}
                useCamera2Api={Platform.OS === 'android'}
                onMountError={(error) => {
                    console.log(error);
                    navigation.goBack();
                    navigation.navigate(Constant.SCREEN_QR);
                }}
                style={StyleSheet.absoluteFillObject}>
            </Camera>

            <View style={styles.reminderContainer}>
                <View style={styles.reminderRect}>
                    <View style={styles.redLine} />
                </View>
                <Text style={styles.reminderText}>{lang?.t('scan_qr_code_to_check_in')}</Text>
            </View>

            {!location && (
                <TouchableOpacity
                    style={styles.locationBanner}
                    onPress={handleEnableLocation}
                    activeOpacity={0.8}
                >
                    <Text style={styles.locationBannerText}>
                        {locationLoading
                            ? lang?.t('obtaining_location')
                            : lang?.t('tap_to_enable_location')}
                    </Text>
                </TouchableOpacity>
            )}
        </View>
    );
}
